import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "../context/auth-context";
import { generateMetaData } from "../util";
import { useIpDetails } from "./location-service";
import { parseApiResponse } from "../models";
import { fetchServiceJson } from "./service-fetch";

const appendQueryParam = (params, key, value) => {
  if (value !== undefined && value !== null && value !== "") {
    params.append(key, value);
  }
};

export async function fetchPublicOrganisationCart({
  profileId,
  accessPointId,
  token,
}) {
  if (!profileId || !accessPointId) return null;

  const response = await fetchServiceJson(
    `collection/organisation/${profileId}/cart?accessPointId=${accessPointId}`,
    token,
  );

  return response ? parseApiResponse(response) : null;
}

export function useProcessCart({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const { orgId, accessPointId, token, cartSource } = params;
      const url = `collection/organisation/${orgId}/cart/process`;
      const urlParams = new URLSearchParams();
      appendQueryParam(urlParams, "accessPointId", accessPointId);
      appendQueryParam(urlParams, "cartSource", cartSource);
      const requestConfig = {
        method: "POST",
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      const endpoint = `${url}?${urlParams.toString()}`;
      return client(endpoint, requestConfig).then(parseApiResponse);
    },
    {
      ...config,
    },
  );
}

export function useUpdateActiveCart({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const { orgId, accessPointId, token, cartSource } = params;
      const url = `collection/organisation/${orgId}/cart`;
      const urlParams = new URLSearchParams();
      appendQueryParam(urlParams, "accessPointId", accessPointId);
      appendQueryParam(urlParams, "cartSource", cartSource);
      const requestConfig = {
        method: "POST",
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      const endpoint = `${url}?${urlParams.toString()}`;
      return client(endpoint, requestConfig).then(parseApiResponse);
    },
    {
      ...config,
    },
  );
}

export function useCartPayment({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/payment/init`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export function useCartPaymentCharge({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/payment/charge`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export function useGetActiveCart({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, accessPointId, token, cartSource }) => {
      if (!orgId || !accessPointId) {
        return Promise.reject(
          new Error("Missing organisation or access point for active cart"),
        );
      }

      const url = `collection/organisation/${orgId}/cart`;
      const params = new URLSearchParams();
      appendQueryParam(params, "accessPointId", accessPointId);
      appendQueryParam(params, "cartSource", cartSource);
      const requestConfig = {
        method: "GET",
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      const endpoint = `${url}?${params.toString()}`;
      return client(endpoint, requestConfig).then(parseApiResponse);
    },
    {
      ...config,
    },
  );
}

export function useCloseCart({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, accessPointId, token, cartSource }) => {
      const url = `collection/organisation/${orgId}/cart/close`;
      const params = new URLSearchParams();
      appendQueryParam(params, "accessPointId", accessPointId);
      appendQueryParam(params, "cartSource", cartSource);
      const requestConfig = {
        method: "GET",
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      const endpoint = `${url}?${params.toString()}`;
      return client(endpoint, requestConfig).then(parseApiResponse);
    },
    {
      ...config,
    },
  );
}

export function useCartSignature({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/cart/signature`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export function useCartCancelPayment({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/cart/cancel`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export const isMarketplaceCartSource = (cartSource) =>
  String(cartSource || "").toUpperCase() === "MARKETPLACE";

export const getUserCollectionsQueryKey = (userId, token, cartSource) =>
  isMarketplaceCartSource(cartSource)
    ? ["marketplaceCollections", userId, token]
    : ["userCollections", userId, token];

export const getGuestCollectionsQueryKey = (token, cartSource) =>
  isMarketplaceCartSource(cartSource)
    ? ["marketplaceCollections", "guest", token]
    : ["guestCollections", token];

export const getCartCollectionsQueryKey = ({ userId, token, cartSource }) =>
  userId
    ? getUserCollectionsQueryKey(userId, token, cartSource)
    : getGuestCollectionsQueryKey(token, cartSource);

export function useFetchUserCollections({
  userId,
  token,
  enabled = false,
  onSuccess,
  onError,
  cartSource,
} = {}) {
  const client = useClient();
  const queryKey = getUserCollectionsQueryKey(userId, token, cartSource);

  const queryFn = async () => {
    if (!userId) throw new Error("Missing userId");
    const url = `collection/user/${userId}/carts?cartSource=${cartSource}`;
    const requestConfig = {
      method: "GET",
    };

    if (token) {
      requestConfig.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    const res = await client(url, requestConfig);
    return parseApiResponse(res);
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!enabled && !!userId,
    onSuccess,
    onError,
  });
}

export function useFetchGuestCollections({
  token,
  enabled = false,
  onSuccess,
  onError,
  cartSource,
} = {}) {
  const client = useClient();
  const queryKey = getGuestCollectionsQueryKey(token, cartSource);

  const queryFn = async () => {
    const url = `collection/carts?cartSource=${cartSource}`;
    const requestConfig = {
      method: "GET",
    };

    if (token) {
      requestConfig.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    const res = await client(url, requestConfig);
    return parseApiResponse(res);
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!enabled,
    onSuccess,
    onError,
  });
}

export function useCartCollections({
  userId,
  token,
  enabled = false,
  onSuccess,
  onError,
  cartSource,
} = {}) {
  const client = useClient();
  const queryKey = getCartCollectionsQueryKey({ userId, token, cartSource });

  const queryFn = async () => {
    const requestConfig = { method: "GET" };

    if (token) {
      requestConfig.headers = {
        Authorization: `Bearer ${token}`,
      };
    }

    const url = userId
      ? `collection/user/${userId}/carts?cartSource=${cartSource}`
      : `collection/carts?cartSource=${cartSource}`;

    const response = await client(url, requestConfig);
    return parseApiResponse(response);
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled,
    onSuccess,
    onError,
  });
}

export function useUserOrders({
  paymentStatus,
  searchTerm,
  page,
  limit,
  accessPointId,
  userId,
  orgId,
  storePath,
  enabled,
  ...config
}) {
  const client = useClient();

  return useQuery(
    [
      "transactions",
      paymentStatus,
      searchTerm,
      page,
      limit,
      accessPointId,
      userId,
      storePath,
    ],
    async () => {
      const queryParams = [
        paymentStatus && `paymentStatus=${paymentStatus}`,
        searchTerm && `searchTerm=${searchTerm}`,
        `limit=${limit}`,
        `page=${page}`,
        storePath !== "market-place" && `accessPointId=${accessPointId}`,
      ].filter(Boolean);

      const baseUrl =
        storePath === "market-place"
          ? `collection/user/${userId}/orders`
          : `collection/organisation/${orgId}/user/${userId}/orders`;

      return client(`${baseUrl}?${queryParams.join("&")}`).then(
        (response) => parseApiResponse(response).data,
      );
    },
    {
      enabled,
      keepPreviousData: true,
      ...config,
    },
  );
}

export function useCollectionInfo({ collectionId, enabled, ...config }) {
  const client = useClient();

  return useQuery(
    ["transaction", collectionId],
    async () =>
      client(`collection/${collectionId}/info`).then((response) => response.data),
    {
      enabled,
      ...config,
    },
  );
}
